/**
 * Hand classification and tagging utilities.
 * Used to enrich quiz answer data for analytics and insights.
 */

export const RANK_VALUES = {
  A: 14, K: 13, Q: 12, J: 11, T: 10,
  9: 9,  8: 8,  7: 7,  6: 6,  5: 5,
  4: 4,  3: 3,  2: 2
};

const SUIT_MAP = {
  '♠': 's', '♣': 'c', '♥': 'h', '♦': 'd',
  s: 's', c: 'c', h: 'h', d: 'd'
};

const BROADWAY = new Set(['A', 'K', 'Q', 'J', 'T']);

// ---------------------------------------------------------------------------
// Hand representation helpers
// ---------------------------------------------------------------------------

/**
 * Convert hand object to exact combo string, e.g. "AsKh".
 */
export function handToCombo(hand) {
  if (!hand?.card1Value || !hand?.card2Value) return '';
  const s1 = SUIT_MAP[hand.card1Suit] || 's';
  const s2 = SUIT_MAP[hand.card2Suit] || 's';
  return `${hand.card1Value}${s1}${hand.card2Value}${s2}`;
}

/**
 * Convert hand object to hand class, e.g. "AKs", "AKo", "AA".
 * Higher-ranked card is always first.
 */
export function handToClass(hand) {
  if (!hand?.card1Value || !hand?.card2Value) return '';
  const c1 = hand.card1Value;
  const c2 = hand.card2Value;
  const r1 = RANK_VALUES[c1] || 0;
  const r2 = RANK_VALUES[c2] || 0;
  const suited = hand.card1Suit === hand.card2Suit;

  if (c1 === c2) return `${c1}${c2}`;

  const [high, low] = r1 >= r2 ? [c1, c2] : [c2, c1];
  return `${high}${low}${suited ? 's' : 'o'}`;
}

/**
 * Get suitedness: 'pair' | 'suited' | 'offsuit'
 */
export function getSuitedness(hand) {
  if (hand?.card1Value === hand?.card2Value) return 'pair';
  return hand?.card1Suit === hand?.card2Suit ? 'suited' : 'offsuit';
}

// ---------------------------------------------------------------------------
// Hand tagging
// ---------------------------------------------------------------------------

/**
 * Get hand category for grouping/insights.
 * Possible values: 'Pocket Pair', 'Broadway', 'Suited Ace', 'Offsuit Ace',
 *   'Suited King', 'Offsuit King', 'Connector', 'One-Gapper',
 *   'Low Suited', 'Low Offsuit', 'Trash'
 */
export function getHandCategory(handClass) {
  if (!handClass) return 'Trash';

  // Pocket pair (length 2, both chars same)
  if (handClass.length === 2 && handClass[0] === handClass[1]) {
    return 'Pocket Pair';
  }

  const suited = handClass.endsWith('s');
  const high = handClass[0];
  const low = handClass[1];
  const r1 = RANK_VALUES[high] || 0;
  const r2 = RANK_VALUES[low] || 0;

  if (high === 'A') return suited ? 'Suited Ace' : 'Offsuit Ace';
  if (high === 'K') return suited ? 'Suited King' : 'Offsuit King';

  // Both Broadway cards (no ace/king already handled above)
  if (BROADWAY.has(high) && BROADWAY.has(low)) return 'Broadway';

  if (suited) {
    const gap = r1 - r2 - 1;
    if (gap === 0) return 'Connector';
    if (gap === 1) return 'One-Gapper';
    return 'Low Suited';
  }

  if (r2 <= 7) return 'Low Offsuit';

  return 'Trash';
}

/**
 * Get rank cluster for performance grouping.
 * Possible values: 'Premium', 'Broadway', 'Middling', 'Low', 'Trash'
 */
export function getRankCluster(handClass) {
  if (!handClass) return 'Trash';

  // Pocket pairs
  if (handClass.length === 2 && handClass[0] === handClass[1]) {
    const r = RANK_VALUES[handClass[0]] || 0;
    if (r >= 10) return 'Premium';  // TT+
    if (r >= 7)  return 'Middling'; // 77-99
    return 'Low';
  }

  const high = handClass[0];
  const low  = handClass[1];
  const r1   = RANK_VALUES[high] || 0;
  const r2   = RANK_VALUES[low]  || 0;
  const suited = handClass.endsWith('s');

  // Premium non-pairs: AJ+, ATs, KQ
  if (r1 === 14 && r2 >= 11) return 'Premium';           // AJ, AQ, AK
  if (r1 === 14 && r2 === 10 && suited) return 'Premium'; // ATs
  if (r1 === 13 && r2 === 12) return 'Premium';           // KQ

  // Broadway
  if (BROADWAY.has(high) && BROADWAY.has(low)) return 'Broadway';

  // Middling
  if (r1 >= 7 && r2 >= 5) return 'Middling';

  // Low
  if (r2 >= 3) return 'Low';

  return 'Trash';
}

// ---------------------------------------------------------------------------
// Action / error derivation
// ---------------------------------------------------------------------------

/**
 * Derive a human-readable action label from betType + frequency.
 * frequency: 0 = fold/check, 50 = mixed, 100 = primary action
 */
export function deriveAction(betType, frequency) {
  if (frequency === 0) return 'Fold';
  if (frequency === 50) return 'Mixed';

  // frequency === 100
  switch (betType) {
    case 'OPEN':
    case 'FACING_OPEN_3BET':
    case 'FACING_3BET_4BET':
      return 'Raise';
    case 'FACING_OPEN_CALL':
    case 'FACING_3BET_CALL':
    case 'LIMP':
    case 'SB_LIMP':
      return 'Call';
    default:
      return 'Jam'; // BLAST / unknown
  }
}

/**
 * Determine error type by comparing correct vs selected action.
 * Returns null if correct.
 */
export function getErrorType(correctAction, selectedAction) {
  if (!correctAction || !selectedAction) return null;
  if (correctAction === selectedAction) return null;

  const aggressive = new Set(['Raise', 'Jam', 'Call']);
  const passive    = new Set(['Fold', 'Check']);

  if (passive.has(correctAction) && aggressive.has(selectedAction)) return 'over_aggressive';
  if (aggressive.has(correctAction) && passive.has(selectedAction)) return 'under_aggressive';
  if (correctAction === 'Mixed') return 'wrong_frequency';
  return 'wrong_action';
}

// ---------------------------------------------------------------------------
// Scenario node
// ---------------------------------------------------------------------------

/**
 * Build a scenario node string from game config.
 * This is the "backbone" that ties all performance data together.
 */
export function deriveScenarioNode(config) {
  if (!config) return null;
  const { betType, heroPosition, openPosition, threeBetPosition } = config;

  // Blast mode
  // Encodes the exact scenario selection (format, stack, position, primary/
  // secondary/tertiary action) so it can be parsed back into a config later
  // (see parseScenarioNode in blastRangeRules.js) — used to drive the
  // Recommended Quiz card on the dashboard with a real, reproducible scenario
  // instead of a random one.
  if (config.quizType === 'BLAST') {
    const stackPart = config.stack ? `${config.stack}` : 'mixed';
    return [
      'BLAST',
      config.format || '',
      stackPart,
      heroPosition || config.position || '',
      config.primaryAction || '',
      config.secondaryAction || '',
      config.tertiaryAction || '',
    ].join('|');
  }

  // Cash game nodes
  switch (betType) {
    case 'OPEN':
      return `${heroPosition} Open`;
    case 'FACING_OPEN_CALL':
      return openPosition
        ? `${heroPosition} vs ${openPosition} Open (Call)`
        : `${heroPosition} Call vs Open`;
    case 'FACING_OPEN_3BET':
      return openPosition
        ? `${heroPosition} vs ${openPosition} Open (3-Bet)`
        : `${heroPosition} 3-Bet vs Open`;
    case 'FACING_3BET_CALL':
      return threeBetPosition
        ? `${heroPosition} vs ${threeBetPosition} 3-Bet (Call)`
        : `${heroPosition} Call vs 3-Bet`;
    case 'FACING_3BET_4BET':
      return threeBetPosition
        ? `${heroPosition} vs ${threeBetPosition} 3-Bet (4-Bet)`
        : `${heroPosition} 4-Bet vs 3-Bet`;
    case 'LIMP':
    case 'SB_LIMP':
      return 'SB Limp';
    default:
      return betType || null;
  }
}
